<template>
  <div>
    <div class="accordion accordion-flush" id="accordion_subscriptions">
      <div
        class="accordion-item"
        v-for="elem in item.subscriptions"
        :key="elem.id"
      >
        <h2 class="accordion-header" :id="`heading${elem.id}`">
          <button
            class="accordion-button d-flex gap-3 collapsed"
            type="button"
            data-bs-toggle="collapse"
            :data-bs-target="`#collapse${elem.id}`"
            aria-expanded="true"
            :aria-controls="`collapse${elem.id}`"
          >
            <div class="d-flex flex-column sub-col" style="min-width: 120px; flex: 1">
              <span class="fw-bold text-muted text-uppercase fs-6 mb-1">Status</span>
              <span class="badge" :class="statusBadgeClass(elem)">{{ elem.formatted_status }}</span>
            </div>
            <div class="d-flex flex-column sub-col" style="min-width: 90px; flex: 0 0 90px">
              <span class="fw-bold text-muted text-uppercase fs-6 mb-1">Início</span>
              <span class="text-secondary">{{ elem.formatted_start_date }}</span>
            </div>
            <div class="d-flex flex-column sub-col" style="min-width: 110px; flex: 0 0 110px">
              <span class="fw-bold text-muted text-uppercase fs-6 mb-1">Cancelamento</span>
              <span :class="elem.canceled_at ? 'text-danger' : 'text-secondary'">{{ elem.formatted_canceled_at }}</span>
            </div>
            <div class="d-flex flex-column sub-col" style="min-width: 110px; flex: 0 0 110px">
              <span class="fw-bold text-muted text-uppercase fs-6 mb-1">Início período</span>
              <span class="text-secondary">{{ elem.formatted_current_period_start }}</span>
            </div>
            <div class="d-flex flex-column sub-col" style="min-width: 110px; flex: 0 0 110px">
              <span class="fw-bold text-muted text-uppercase fs-6 mb-1">Fim período</span>
              <span class="text-secondary">{{ elem.formatted_current_period_end }}</span>
            </div>
            <div class="d-flex flex-column sub-col" style="min-width: 140px; flex: 1">
              <span class="fw-bold text-muted text-uppercase fs-6 mb-1">Plano</span>
              <span class="text-secondary">{{ elem.formatted_plan_frequency }}</span>
            </div>
            <div class="d-flex flex-column sub-col" style="min-width: 80px; flex: 0 0 80px">
              <span class="fw-bold text-muted text-uppercase fs-6 mb-1">Valor</span>
              <span class="text-secondary">{{ elem.formatted_unit_amount }}</span>
            </div>
            <div class="d-flex flex-column align-items-center" style="flex: 0 0 40px">
              <a
                :href="stripeSubscriptionLink(elem)"
                target="_blank"
                @click.stop
              >
                <i class="fa-brands fa-cc-stripe fa-xl text-dark"></i>
              </a>
            </div>
          </button>
        </h2>
        <div
          :id="`collapse${elem.id}`"
          class="accordion-collapse collapse"
          :aria-labelledby="`heading${elem.id}`"
          data-bs-parent="#accordion_subscriptions"
        >
          <div class="accordion-body p-0">
            <div class="table-responsive">
              <table class="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Data da transação</th>
                    <th>Status da transação</th>
                    <th class="text-end">Valor</th>
                    <th v-if="shouldShowCharges(elem.status)">Tentativas de cobrança</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="invoice in elem.invoices" :key="invoice.id">
                    <td>{{ invoice.formatted_created }}</td>
                    <td>{{ invoice.formatted_status }}</td>
                    <td>{{ invoice.formatted_total }}</td>
                    <td v-if="shouldShowCharges(elem.status)">
                      <span
                        v-if="invoice.loadingCharges"
                        class="spinner-border spinner-border-sm text-muted"
                        role="status"
                      ></span>
                      <div v-else-if="invoice.charges && invoice.charges.length" class="d-flex flex-column gap-1">
                        <div
                          v-for="(charge, cIdx) in invoice.charges"
                          :key="cIdx"
                          class="charge-row"
                          :class="charge.status === 'failed' ? 'charge-failed' : 'charge-success'"
                        >
                          <i
                            class="fa-solid fa-xs me-1"
                            :class="charge.status === 'failed' ? 'fa-circle-xmark text-danger' : 'fa-circle-check text-success'"
                          ></i>
                          <span class="text-muted me-2">{{ charge.created ? $moment(charge.created).format('DD/MM/YYYY HH:mm') : '---' }}</span>
                          <span v-if="charge.card" class="me-2">
                            <span class="text-capitalize">{{ charge.card.brand }}</span>
                            •••• {{ charge.card.last4 }}
                          </span>
                          <span
                            v-if="charge.error_translated"
                            class="charge-error"
                            :class="charge.status === 'failed' ? 'text-danger' : 'text-muted'"
                          >— {{ charge.error_translated }}</span>
                        </div>
                      </div>
                      <span v-else class="text-muted" style="font-size: 0.8rem">---</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import NetworkService from "@/services/NetworkService";

export default defineComponent({
  name: "PsiDetailSubscriptions",
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      network: new NetworkService(),
    };
  },
  methods: {
    shouldShowCharges(status) {
      return status === "incomplete" || status === "incomplete_expired" || status === "past_due";
    },
    async loadChargesForIncompleteInvoices() {
      if (!this.item?.subscriptions) return;

      for (const sub of this.item.subscriptions) {
        if (!this.shouldShowCharges(sub.status)) continue;
        if (!sub.invoices?.length) continue;

        for (const invoice of sub.invoices) {
          if (invoice.status === "paid") continue;

          this.$set(invoice, "loadingCharges", true);
          this.$set(invoice, "charges", []);

          try {
            const res = await this.network.get(
              `api/subscription-invoices/${invoice.id}`
            );
            this.$set(invoice, "charges", res.data?.charges || []);
          } catch (e) {
            console.error("Erro ao buscar charges da invoice:", e);
          } finally {
            this.$set(invoice, "loadingCharges", false);
          }
        }
      }
    },
    formatCharge(charge) {
      const date = charge.created
        ? this.$moment(charge.created).format("DD/MM/YYYY HH:mm")
        : "---";

      let cardInfo = "";
      if (charge.card) {
        const brand = charge.card.brand || "";
        const last4 = charge.card.last4 || "";
        const expMonth = charge.card.exp_month || "";
        const expYear = charge.card.exp_year || "";
        cardInfo = `Cartão ${brand} final ${last4} (${expMonth}/${expYear})`;
      }

      const status = charge.error_translated || "---";

      if (cardInfo) {
        return `${date} - ${cardInfo} com status "${status}"`;
      }
      return `${date} - Status "${status}"`;
    },
    stripeSubscriptionLink(elem) {
      const acctId = process.env.VUE_APP_STRIPE_PLATFORM_ACCOUNT_ID;
      const mid =
        process.env.VUE_APP_ENV === "prod"
          ? "subscriptions"
          : "test/subscriptions";
      return `https://dashboard.stripe.com/${acctId}/${mid}/${elem.subscription_id}`;
    },
    statusBadgeClass(elem) {
      if (
        elem.status === "canceled" &&
        elem.trial_end &&
        this.$moment(elem.trial_end).isAfter(this.$moment())
      ) {
        return "bg-primary";
      }
      if (elem.status === "active" && elem.canceled_at) {
        return "bg-warning text-dark";
      }
      const classes = {
        active: "bg-success",
        trialing: "bg-primary",
        canceled: "bg-danger",
        past_due: "bg-warning text-dark",
        unpaid: "bg-warning text-dark",
        paused: "bg-secondary",
        incomplete: "bg-warning text-dark",
        incomplete_expired: "bg-danger",
      };
      return classes[elem.status] || "bg-secondary";
    },
  },
});
</script>

<style scoped>
.charge-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 0.78rem;
  padding: 4px 8px;
  border-left: 3px solid transparent;
  border-radius: 2px;
}
.charge-failed {
  border-left-color: #dc3545;
  background-color: #fff5f5;
}
.charge-success {
  border-left-color: #198754;
}
.charge-error {
  font-weight: 600;
}
</style>
